/**
 * opora-consent.js — Build-33 Q16 + COOKIE-OVERLAY-FIX + COOKIE-GEO-GATE 2026-06-17
 * GDPR/CCPA cookie consent — vanilla JS, no deps, ≤12KB
 *
 * COOKIE-OVERLAY-FIX: Banner shows WITHOUT the full-screen overlay.
 * Overlay is only used for the preferences modal (which IS a blocking dialog).
 * Per user directive: "dont ever cover the screen with cookie prototype,
 * they can choose accept/reject."
 *
 * COOKIE-GEO-GATE: Banner only shown to legally-required regions:
 *   EU/EEA/UK (GDPR/UK GDPR), California (CCPA/CPRA)
 *   All other visitors get implicit essential-only; can opt in via footer link.
 *
 * Architecture:
 *  - OporaConsent singleton manages state
 *  - Reads/writes localStorage + 1-year first-party cookie
 *  - Honors Sec-GPC: 1 (Global Privacy Control) — auto-denies marketing
 *  - GTM consent mode v2 wiring
 *  - Klaviyo deferred load
 *  - Banner: bottom-sheet mobile / slim corner card bottom-left desktop — NO BACKDROP
 *  - Modal: full category preferences — backdrop active (clicks-to-close behavior)
 */

(function () {
  'use strict';

  /* ── Constants ─────────────────────────────────────────────── */
  var CONSENT_VERSION = 1;
  var COOKIE_NAME     = 'opora_consent_v1';
  var LS_KEY          = 'opora_consent_v1';
  var COOKIE_DAYS     = 365;

  /* ── Detect Global Privacy Control ─────────────────────────── */
  function isGPCActive() {
    return navigator.globalPrivacyControl === true;
  }

  /* ── Cookie helpers ────────────────────────────────────────── */
  function setCookie(name, value, days) {
    var expires = new Date();
    expires.setTime(expires.getTime() + days * 864e5);
    document.cookie = name + '=' + encodeURIComponent(value)
      + '; expires=' + expires.toUTCString()
      + '; path=/; SameSite=Lax';
  }

  function getCookie(name) {
    var match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([.*+?^=!:${}()|[\]\/\\])/g, '\\$1') + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  /* ── Consent state ────────────────────────────────────────── */
  function loadConsent() {
    var raw = null;
    try { raw = localStorage.getItem(LS_KEY); } catch (e) {}
    if (!raw) raw = getCookie(COOKIE_NAME);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  function saveConsent(state) {
    var payload = JSON.stringify(state);
    try { localStorage.setItem(LS_KEY, payload); } catch (e) {}
    setCookie(COOKIE_NAME, payload, COOKIE_DAYS);
  }

  function buildConsentState(analytics, marketing) {
    return {
      essential: true,
      analytics: analytics ? 1 : 0,
      marketing: marketing ? 1 : 0,
      ts: Math.floor(Date.now() / 1000),
      version: CONSENT_VERSION
    };
  }

  /* ── GTM consent mode v2 ──────────────────────────────────── */
  function pushConsentDefault() {
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    gtag('consent', 'default', {
      analytics_storage:  'denied',
      ad_storage:         'denied',
      ad_user_data:       'denied',
      ad_personalization: 'denied',
      wait_for_update:    500
    });
  }

  function pushConsentUpdate(state) {
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    /*
      Implicit records are non-gated defaults (no banner), not opt-outs.
      Grant analytics_storage for them so GA4 Consent Mode does not park
      pageviews as cookieless G100 pings that barely show in reports.
      Explicit reject (analytics:0 without implicit) still denies.
    */
    var analyticsGranted = !!(state && (state.analytics || state.implicit));
    gtag('consent', 'update', {
      analytics_storage:  analyticsGranted ? 'granted' : 'denied',
      ad_storage:         state.marketing ? 'granted' : 'denied',
      ad_user_data:       state.marketing ? 'granted' : 'denied',
      ad_personalization: state.marketing ? 'granted' : 'denied'
    });
  }

  /*
    Shopify's Google & YouTube pixel often pushes a late consent `default`
    (all granted) after our update. Re-assert our update so ads stay denied
    when marketing is off and analytics stays granted when allowed.
  */
  var _consentReassertTimer = null;
  function reassertConsentAfterPixel(state) {
    if (_consentReassertTimer) {
      clearInterval(_consentReassertTimer);
      _consentReassertTimer = null;
    }
    var n = 0;
    _consentReassertTimer = setInterval(function () {
      pushConsentUpdate(state);
      if (++n >= 8) {
        clearInterval(_consentReassertTimer);
        _consentReassertTimer = null;
      }
    }, 500);
  }

  /*
    Shopify's GA pixel runs in a sandbox and often never sends engagement
    time (_et / user_engagement) for single-page visits, so sessions stay
    seg=0 even after real scroll/read time. Heartbeat on the main page when
    analytics is allowed; require a real interaction so thin bot Direct does
    not get free engaged sessions.
  */
  var _engHb = null;
  function stopEngagementHeartbeat() {
    if (!_engHb) return;
    try {
      if (_engHb.interval) clearInterval(_engHb.interval);
      (_engHb.offs || []).forEach(function (off) { try { off(); } catch (e) {} });
    } catch (eStop) { /* ignore */ }
    _engHb = null;
    try { window.__oporaEngHbStarted = false; } catch (eFlag) { /* ignore */ }
  }

  function startEngagementHeartbeat(state) {
    var analyticsGranted = !!(state && (state.analytics || state.implicit));
    if (!analyticsGranted) {
      stopEngagementHeartbeat();
      return;
    }
    if (_engHb || window.__oporaEngHbStarted) return;
    window.__oporaEngHbStarted = true;

    var unsentMs = 0;
    var lastTick = Date.now();
    var pageVisible = document.visibilityState !== 'hidden';
    var hadInteraction = false;
    var offs = [];

    function gtagPush() {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(arguments);
    }

    function tick() {
      var now = Date.now();
      if (pageVisible) unsentMs += (now - lastTick);
      lastTick = now;
    }

    function markInteraction() {
      hadInteraction = true;
    }

    function flush() {
      tick();
      if (!hadInteraction) return;
      var ms = Math.floor(unsentMs);
      if (ms < 1000) return;
      unsentMs = 0;
      try {
        gtagPush('event', 'user_engagement', { engagement_time_msec: ms });
      } catch (eFlush) { /* analytics never breaks UX */ }
    }

    ['scroll', 'keydown', 'pointerdown', 'touchstart', 'click'].forEach(function (type) {
      var opts = { passive: true, capture: true };
      window.addEventListener(type, markInteraction, opts);
      offs.push(function () { window.removeEventListener(type, markInteraction, opts); });
    });

    function onVis() {
      tick();
      pageVisible = document.visibilityState !== 'hidden';
      if (!pageVisible) flush();
    }
    document.addEventListener('visibilitychange', onVis);
    offs.push(function () { document.removeEventListener('visibilitychange', onVis); });

    function onHide() { flush(); }
    window.addEventListener('pagehide', onHide);
    offs.push(function () { window.removeEventListener('pagehide', onHide); });

    var interval = setInterval(function () {
      tick();
      /* ~10s visible + interaction → GA4 engaged-session threshold */
      if (hadInteraction && unsentMs >= 10000) flush();
    }, 2500);

    _engHb = { interval: interval, offs: offs };
  }

  /* ── GTM deferred load ─────────────────────────────────────── */
  function loadGTM() {
    if (window._oc_gtm_loaded) return;
    window._oc_gtm_loaded = true;
    // GTM is injected via content_for_header in Shopify — no extra load needed here.
    // consent update is already pushed above; GTM container respects it.
  }

  /* ── Klaviyo deferred load ───────────────────────────────────── */
  function loadKlaviyo() {
    if (window._oc_klaviyo_loaded) return;
    window._oc_klaviyo_loaded = true;
    var apiKey = window.OPORA_KLAVIYO_KEY;
    if (!apiKey) return;
    var s = document.createElement('script');
    s.async = true;
    s.src = 'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=' + apiKey;
    document.head.appendChild(s);
  }

  /* ── Apply consent decisions ──────────────────────────────────── */
  function applyConsent(state) {
    pushConsentUpdate(state);
    reassertConsentAfterPixel(state);
    loadGTM();
    if (state.marketing) {
      loadKlaviyo();
    }
    /* Phase D parks GA/WPM until unlock; release as soon as a decision exists. */
    try {
      if (window.OporaPerfBoot && typeof window.OporaPerfBoot.unlockTracking === 'function') {
        window.OporaPerfBoot.unlockTracking();
      }
    } catch (e) { /* consent never breaks UX */ }
    try {
      startEngagementHeartbeat(state);
    } catch (eEng) { /* analytics never breaks UX */ }
  }

  /* ── DOM helpers ──────────────────────────────────────────── */
  function $(id) { return document.getElementById(id); }

  /* ── Show / hide ────────────────────────────────────────────
     COOKIE-OVERLAY-FIX: Banner shows without overlay backdrop.
     Overlay element stays hidden (and never blocks pointer events)
     unless the preferences modal is open. */
  function showBanner() {
    var banner = $('oc-banner');
    if (!banner) return;
    banner.removeAttribute('hidden');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        banner.classList.add('oc-visible');
      });
    });
    banner.setAttribute('aria-hidden', 'false');
    // NOTE: Overlay deliberately NOT shown here. Banner is a corner card,
    // not a modal. Backdrop only attaches when prefs modal opens.
  }

  function hideBanner() {
    var banner = $('oc-banner');
    if (!banner) return;
    banner.classList.remove('oc-visible');
    banner.setAttribute('aria-hidden', 'true');
    setTimeout(function () {
      banner.setAttribute('hidden', '');
    }, 250);
  }

  function showModal(currentState) {
    var wrap    = $('oc-modal-wrap');
    var overlay = $('oc-overlay');
    if (!wrap) return;
    var analyticsToggle = $('oc-toggle-analytics');
    var marketingToggle = $('oc-toggle-marketing');
    if (analyticsToggle) {
      analyticsToggle.checked = currentState ? !!currentState.analytics : false;
    }
    if (marketingToggle) {
      marketingToggle.checked = currentState ? !!currentState.marketing : false;
    }
    wrap.removeAttribute('hidden');
    // COOKIE-OVERLAY-FIX: Show overlay ONLY when modal opens.
    // Add --modal modifier to enable pointer-events:all for click-to-close.
    if (overlay) {
      overlay.removeAttribute('hidden');
      overlay.classList.add('oc-overlay--modal');
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        wrap.classList.add('oc-visible');
        if (overlay) overlay.classList.add('oc-visible');
      });
    });
    wrap.setAttribute('aria-hidden', 'false');
    setTimeout(function () {
      var firstFocusable = wrap.querySelector('button, [href], input, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) firstFocusable.focus();
    }, 50);
  }

  function hideModal() {
    var wrap    = $('oc-modal-wrap');
    var overlay = $('oc-overlay');
    if (!wrap) return;
    wrap.classList.remove('oc-visible');
    if (overlay) overlay.classList.remove('oc-visible');
    wrap.setAttribute('aria-hidden', 'true');
    setTimeout(function () {
      wrap.setAttribute('hidden', '');
      if (overlay) {
        overlay.setAttribute('hidden', '');
        overlay.classList.remove('oc-overlay--modal');
      }
    }, 250);
  }

  /* ── Decision handlers ────────────────────────────────────────── */
  function acceptAll() {
    var gpc = isGPCActive();
    var state = buildConsentState(!gpc, !gpc);
    if (gpc) state.marketing = 0;
    saveConsent(state);
    applyConsent(state);
    hideBanner();
    hideModal();
  }

  function rejectAll() {
    var state = buildConsentState(false, false);
    saveConsent(state);
    applyConsent(state);
    hideBanner();
    hideModal();
  }

  function savePreferences() {
    var analyticsToggle = $('oc-toggle-analytics');
    var marketingToggle = $('oc-toggle-marketing');
    var gpc = isGPCActive();
    var analytics = analyticsToggle ? analyticsToggle.checked : false;
    var marketing  = (marketingToggle ? marketingToggle.checked : false) && !gpc;
    var state = buildConsentState(analytics, marketing);
    saveConsent(state);
    applyConsent(state);
    hideBanner();
    hideModal();
  }

  /* ── Keyboard focus trap in modal ─────────────────────────────── */
  function trapFocus(e) {
    var wrap = $('oc-modal-wrap');
    if (!wrap || !wrap.classList.contains('oc-visible')) return;
    var focusables = wrap.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    var first = focusables[0];
    var last  = focusables[focusables.length - 1];
    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    if (e.key === 'Escape') {
      hideModal();
    }
  }

  /* ── COOKIE-GEO-GATE: Only show banner to legally-required regions ── */
  function shouldShowBanner(callback) {
    // Server-rendered Liquid hint via window.OPORA_GEO_HINT (see snippets/opora-consent-banner.liquid)
    var liquidCountry = window.OPORA_GEO_HINT && window.OPORA_GEO_HINT.country;

    // EU/EEA/UK list (ISO 3166-1 alpha-2)
    var EU_EEA_UK = ['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE','IS','LI','NO','GB'];

    // Quick win: if Liquid already gave us EU/UK, show immediately
    if (liquidCountry && EU_EEA_UK.indexOf(liquidCountry) !== -1) {
      return callback(true, 'liquid-eu');
    }

    // California proxy (Liquid has no state). Other US states: no banner.
    if (!liquidCountry || liquidCountry === 'US') {
      try {
        var tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz === 'America/Los_Angeles') {
          return callback(true, 'tz-california-proxy');
        }
      } catch (e) {}
      if (liquidCountry === 'US') {
        return callback(false, 'us-non-ca');
      }
    }

    // Fallback: hit Shopify's browsing_context_suggestions.json for definitive geo
    // (this is what Shopify itself uses; first-party, no CORS issue)
    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'browsing_context_suggestions.json'
      : '/browsing_context_suggestions.json')
      .then(function(r){ return r.json(); })
      .then(function(d){
        var iso = d && d.detected_values && d.detected_values.country && d.detected_values.country.handle;
        if (iso && EU_EEA_UK.indexOf(iso) !== -1) return callback(true, 'shopify-eu');
        // Honor Sec-GPC always — if user explicitly signals "no tracking", we still show banner
        // so they can review and confirm preferences (or just close — defaults are denied)
        if (navigator.globalPrivacyControl === true) return callback(true, 'sec-gpc');
        callback(false, 'non-gated-region');
      })
      .catch(function(){
        // Fail closed for non-gated regions — do not show banner to random US states on network failure
        callback(false, 'geo-fetch-failed-closed');
      });
  }

  /* ── Wire button listeners ────────────────────────────────────── */
  function wireButtons() {
    var btnAccept     = $('oc-btn-accept');
    var btnReject     = $('oc-btn-reject');
    var btnPrefs      = $('oc-btn-prefs');
    var btnCloseModal = $('oc-modal-close');
    var btnSavePrefs  = $('oc-btn-save-prefs');
    var btnAcceptAll2 = $('oc-btn-accept-all2');
    var overlay       = $('oc-overlay');

    if (btnAccept)    btnAccept.addEventListener('click', acceptAll);
    if (btnReject)    btnReject.addEventListener('click', rejectAll);
    if (btnPrefs)     btnPrefs.addEventListener('click', function () {
      hideBanner();
      showModal(loadConsent());
    });
    if (btnCloseModal) btnCloseModal.addEventListener('click', hideModal);
    if (btnSavePrefs)  btnSavePrefs.addEventListener('click', savePreferences);
    if (btnAcceptAll2) btnAcceptAll2.addEventListener('click', acceptAll);

    // COOKIE-OVERLAY-FIX: click overlay backdrop to close modal (modal state only)
    if (overlay) {
      overlay.addEventListener('click', function () {
        if (overlay.classList.contains('oc-overlay--modal')) hideModal();
      });
    }

    document.addEventListener('keydown', trapFocus);
  }

  /* ── Init ──────────────────────────────────────────────────────────── */
  function init() {
    pushConsentDefault();

    var existing = loadConsent();
    var gpc      = isGPCActive();

    if (gpc && existing && existing.marketing) {
      existing.marketing = 0;
      saveConsent(existing);
    }

    if (existing && existing.version === CONSENT_VERSION) {
      /* Returning US visitors still have the old implicit analytics:0 cookie. */
      if (existing.implicit && !existing.analytics) {
        existing.analytics = 1;
        saveConsent(existing);
      }
      applyConsent(existing);
      // Still wire buttons — user may open modal via footer link
      wireButtons();
      return;
    }

    // COOKIE-GEO-GATE: only show banner if visitor is in a legally-required region
    shouldShowBanner(function(show, reason) {
      console.log(JSON.stringify({ _event: 'consent.geo_gate', show: show, reason: reason, ts: Date.now() }));
      if (show) {
        setTimeout(function(){ showBanner(); }, 400);
      } else {
        // Non-gated: analytics allowed by default (no banner). Marketing stays off.
        var implicit = buildConsentState(true, false);
        implicit.implicit = 1; // marker so we can distinguish from explicit accept
        saveConsent(implicit);
        applyConsent(implicit);
      }
    });

    wireButtons();
  }

  /* ── Expose public API for footer links ────────────────────────── */
  window.OporaConsent = {
    openPreferences: function () { showModal(loadConsent()); },
    getState:        loadConsent
  };

  /* ── Wire footer preference links that load after init ─────────────── */
  document.addEventListener('click', function (e) {
    var el = e.target.closest('[data-oc-open-prefs]');
    if (el) {
      e.preventDefault();
      showModal(loadConsent());
    }
  });

  /* ── Bootstrap ────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
